import type { ServicePageConfig } from "../../../types";
import {
  getPackagePricingRows,
  PACKAGE_PRICE_HINT_SV,
  PACKAGE_PRICE_HINT_EN,
} from "../../../package-pricing";
import { getCityMunicipalityList } from "../../../cities";

const stockholmKommunerText = getCityMunicipalityList("stockholm");

export const flytthjalpNackaPage: ServicePageConfig = {
  slug: "flytthjalp-nacka",
  serviceCategory: "flytt",
  city: "stockholm",

  meta: {
    title: "Flytthjälp Nacka – Skåpbil, Bärare, Fast Pris | Ungdomskraft",
    description:
      "Flytthjälp i Nacka: Sickla, Kvarnholmen, Nacka strand, Järla, Fisksätra, Orminge, Boo, Älta och Saltsjöbaden. Skåpbil och bärare, RUT-avdrag direkt.",
    h1: "Flytthjälp Nacka",
  },

  hero: {
    heading: "Flytthjälp i Nacka",
    intro:
      "Från nybygget på Kvarnholmen till sjötomten i Saltsjöbaden – Ungdomskraft flyttar ditt hem i hela Nacka kommun. Vi bär ut i Sickla, Järla, Finntorp, Fisksätra, Orminge, Björknäs eller Älta, kör skåpbilen till nya adressen och ställer allt på plats. Du ser priset när du bokar och RUT-avdraget dras direkt.",
    heroImage: "/hero-flytthjalp.png",
    heroImageAlt: "Flytthjälp i Nacka – bärare lastar möbler i en skåpbil",
  },

  featureCards: {
    sinceText: "Flytthjälp sedan 2014",
    storyText:
      "Ungdomskraft har hjälpt människor att flytta sedan 2014. Nacka är en kommun med branta tomter, trappor ner mot vattnet och höghus på berget, och våra bärare är vana vid alla varianterna – liksom vid köerna på Värmdöleden och när de är värda att undvika.",
    bookYourText: "Boka flytt i Nacka",
    bookDescText:
      "Skriv var du flyttar från och till, vad som ska med och när. Priset syns på en gång – bärare och skåpbil ingår.",
    vehicleTitleText: "Vi kör, du slipper",
    vehicleDescText:
      "Skåpbilen och bärarna kommer tillsammans. Vi lastar, kör över Skurubron eller in mot stan och lossar vid nya dörren, så du inte behöver hyra eller köra något själv.",
  },

  itemsSection: {
    heading: "Saker vi ofta flyttar i Nacka",
    items: [
      {
        name: "Soffor & fåtöljer",
        description: "Soffor och fåtöljer som bärs uppför trappor och slänter",
        image: "/sofa.webp",
        alt: "Soffa som bärs vid flytthjälp i Saltsjöbaden, Nacka",
      },
      {
        name: "Sängar & madrasser",
        description: "Sängar, madrasser och barnsängar till nya sovrum",
        image: "/bed.webp",
        alt: "Säng som lastas vid flytt från Fisksätra i Nacka",
      },
      {
        name: "Bord & stolar",
        description: "Matbord, altanmöbler och arbetsbord",
        image: "/table.webp",
        alt: "Matbord och stolar vid flytt i Sickla, Nacka",
      },
      {
        name: "Vitvaror",
        description: "Tvättmaskin, kyl, frys och diskmaskin",
        image: "/washer.webp",
        alt: "Vitvaror som flyttas vid flytthjälp i Orminge, Nacka",
      },
      {
        name: "Garderober & byråer",
        description: "Garderober, byråer och hyllsystem som tas isär",
        image: "/wardrobe.webp",
        alt: "Garderob som demonteras vid flytt på Kvarnholmen i Nacka",
      },
      {
        name: "Flyttkartonger",
        description: "Kartonger, båt- och sommarsaker från förråd och garage",
        image: "/box.webp",
        alt: "Flyttkartonger som bärs vid villaflytt i Boo, Nacka",
      },
    ],
  },

  seoContent: {
    mainHeading: "Flytthjälp i Nacka – nybyggen på Sicklaön, villor i Boo och Saltsjöbaden",
    mainText:
      "Nacka är en av de kommuner i länet som byggs mest just nu, och det syns på vilka flyttar vi gör. På Sicklaön har gamla industriområden blivit bostäder: Sickla växte fram kring det tidigare Atlas Copco-området, Kvarnholmen har gått från kvarnar och industri till lägenheter vid vattnet, och i Nacka strand byggs kontorshus om till bostäder. Samtidigt byggs tunnelbanan ut till Nacka centrum, och kring Järla och Nacka Forum växer nya kvarter. I de nya husen är hissarna rymliga men lastning sker ofta via garage eller särskilda lastplatser, och flyttbilen behöver planeras in.\n\nLängre ut ser Nacka helt annorlunda ut. Fisksätra byggdes på 1970-talet med flerbostadshus på höjden ovanför Baggensfjärden, och Orminge i Boo är i huvudsak från 1960- och 70-talen. Runt dem ligger villaområden i Boo, Björknäs, Älta, Saltsjö-Duvnäs och Saltsjöbaden, där tomterna ofta lutar och trapporna mellan gata och entré kan vara många. Där bär vi längre sträckor, och det är ofta källare, garage och båtprylar som gör flytten stor. Vanliga flyttar är unga som flyttar in i en första lägenhet på Sicklaön, familjer som går från lägenhet till villa i Boo och äldre som byter huset i Saltsjöbaden mot en lägenhet med hiss.\n\nTrafiken styr mycket av en flyttdag i Nacka. Värmdöleden går genom kommunen och Skurubron över Skurusundet är den stora flaskhalsen mot Boo, medan Södra länken leder in mot Stockholm från Sickla. Saltsjöbanan går genom kommunen ut till Saltsjöbaden och Solsidan, och tvärbanan når Sickla. Vi lägger rutten så att bilen inte står i rusningskön och bokar in rätt antal bärare efter hur långt vi behöver bära från bilen.",
    preparationHeading: "Förbered flytten i Nacka",
    preparationText:
      "Tänk på vägen mellan bil och dörr. Bor du i villa med trappor ner mot huset eller en lång gång över tomten, skriv det i bokningen så att vi skickar rätt antal bärare. Flyttar du in i en nyproduktion på Sicklaön eller Kvarnholmen, ta reda på hur lastning till huset går till och om garaget har en höjdbegränsning.\n\nPacka kartongerna i förväg och märk dem med rum. Samla det som ska med från förråd, garage och båthus på ett ställe. Frosta av kyl och frys dygnet innan och töm tvättmaskinen på vatten. Ha portkoder, nycklar och garagetaggar redo för båda adresserna.",
    additionalSections: [
      {
        heading: "Kommundelar i Nacka där vi flyttar",
        content:
          "Vi hjälper till med flytt i hela Nacka. På Sicklaön flyttar vi i Sickla, Järla, Finntorp, Ektorp, Henriksdal, Jarlaberg, Kvarnholmen, Nacka strand och kring Nacka Forum, både i äldre hyreshus och i nyproduktion.\n\nI Boo tar vi flyttar i Orminge, Björknäs, Saltsjö-Boo, Kummelnäs och Tollare. I söder flyttar vi i Älta, och i sydost i Fisksätra, Saltsjöbaden och Solsidan. Körsträcka inom 20 km ingår, vilket räcker för flyttar inom Nacka och till större delen av Stockholm.",
      },
      {
        heading: "Flytta från lägenhet till villa – eller tvärtom",
        content:
          "Många flyttar i Nacka går mellan boendeformer. En familj lämnar lägenheten i Sickla för ett hus i Boo, eller ett par säljer villan i Saltsjöbaden och flyttar till en lägenhet vid Nacka centrum. Då finns det ofta mer bohag än det får plats i nya hemmet, och vi kan köra det som inte ska med till återvinning eller återbruk under samma dag.\n\nFlytthjälp ger rätt till RUT-avdrag. Du får 50 % avdrag på arbetskostnaden, upp till 75 000 kr per person och år, och vi drar av det direkt på fakturan.",
        cta: { text: "Se bortforsling i Nacka →", href: "/bortforsling-nacka" },
      },
      {
        heading: "Varför boka flytthjälp i Nacka med Ungdomskraft?",
        listItems: [
          {
            label: "Vi känner Nacka –",
            text: "Från nybyggena på Kvarnholmen till slänttomterna i Saltsjöbaden. Vi vet var bilen kan stå och hur långt det är att bära.",
          },
          {
            label: "Planerad körning –",
            text: "Vi lägger flytten efter Värmdöleden och Skurubron, så tiden går till att bära och inte till att stå i kö.",
          },
          {
            label: "Fast pris –",
            text: "Du ser priset i bokningen innan du bekräftar. Inga tillägg för trappor eller långa bärsträckor.",
          },
          {
            label: "Försäkrat –",
            text: "Alla flyttar är försäkrade genom Trygg Hansa, både ditt bohag och fastigheten vi bär i.",
          },
          {
            label: "Allt med i bilen –",
            text: "Skåpbil, bärare, filtar, remmar och golvskydd ingår.",
          },
        ],
      },
    ],
  },

  faq: [
    {
      question: "Vad kostar flytthjälp i Nacka?",
      answer:
        "Enstaka möbler bokas som Liten (995 kr) eller Mellan (1 495 kr). En fullt möblerad etta eller tvåa brukar kosta 2 495–4 995 kr och en hel villa bokas som Mega Stor (8 995 kr). Körning inom 20 km ingår och du ser priset när du bokar.",
    },
    {
      question: "Kan jag få RUT-avdrag på flytthjälp i Nacka?",
      answer:
        "Ja. Du får 50 % avdrag på arbetskostnaden, upp till 75 000 kr per person och år, och vi drar av det direkt på fakturan.",
    },
    {
      question: "Hur snabbt kan ni komma till Nacka?",
      answer:
        "Vi har ofta lediga tider samma vecka, ibland samma dag. Välj datum i bokningen så ser du tiderna.",
    },
    {
      question: "Vårt hus ligger nedanför en lång trappa – går det?",
      answer:
        "Ja, det är vanligt i Nacka. Skriv i bokningen hur långt det är från bilen till entrén, så skickar vi tillräckligt många bärare.",
    },
    {
      question: "Kan ni flytta in i nyproduktion på Kvarnholmen eller i Sickla?",
      answer:
        "Ja. Tala om i bokningen hur lastning sker, till exempel via garage, och om det finns en bokad inflyttningstid.",
    },
    {
      question: "Flyttar ni en hel villa i Boo eller Saltsjöbaden?",
      answer:
        "Ja, med garage, källare och förråd. Stora hus bokas som Extra Stor eller Mega Stor, och vi gör flera vändor vid behov.",
    },
    {
      question: "Bär ni i hus utan hiss i Fisksätra och Orminge?",
      answer: "Ja, vi bär i trapphus med och utan hiss utan extra kostnad.",
    },
    {
      question: "Tar ni isär och monterar möbler?",
      answer:
        "Ja, vi demonterar sängar, garderober och bord när de inte går ut hela och sätter ihop dem igen på nya adressen.",
    },
    {
      question: "Kan ni flytta vitvaror?",
      answer:
        "Ja, tvättmaskin, torktumlare, kyl, frys och diskmaskin. Frosta av kyl och frys dygnet innan.",
    },
    {
      question: "Går det att flytta på helgen?",
      answer: "Ja, vi flyttar alla dagar i veckan.",
    },
    {
      question: "Kan ni köra bort det som inte ska med till nya hemmet?",
      answer:
        "Ja, boka en bortforsling för samma dag så kör vi det som inte ska med till återvinning eller återbruk.",
    },
    {
      question: "Vilka delar av Nacka täcker ni?",
      answer:
        "Hela kommunen: Sicklaön (Sickla, Järla, Finntorp, Ektorp, Henriksdal, Jarlaberg, Kvarnholmen, Nacka strand), Boo (Orminge, Björknäs, Saltsjö-Boo, Kummelnäs, Tollare), Älta, Fisksätra och Saltsjöbaden.",
    },
    {
      question: "Vilka kommuner i övrigt flyttar ni i?",
      answer: `Vi hjälper till med flytt i hela Stockholmsregionen: ${stockholmKommunerText}. Körsträcka inom 20 km ingår alltid.`,
    },
  ],

  priceHint: PACKAGE_PRICE_HINT_SV.transport,

  pricingRows: getPackagePricingRows("transport", "sv"),

  dynamicHeadings: {
    threeSteps: "Flytthjälp i Nacka i 3 steg",
    step2Text:
      "Bärarna skyddar möbler och golv, bär ut allt och lastar skåpbilen. Vi kör till nya adressen – inom Nacka eller vidare in mot stan.",
    step3Title: "Inne i nya hemmet",
    step3Desc:
      "Vi bär in och ställer allt på rätt plats. Möbler som togs isär monteras ihop igen innan vi lämnar.",
    ctaHeading: "Boka flytthjälp i Nacka",
    ctaText:
      "Fyll i adresser, paket och tid. Du ser priset direkt och RUT-avdraget dras på fakturan.",
  },

  en: {
    slug: "moving-help-nacka",
    meta: {
      title: "Moving Help Nacka – Van, Movers, Fixed Price | Ungdomskraft",
      description:
        "Moving help in Nacka: Sickla, Kvarnholmen, Nacka strand, Järla, Fisksätra, Orminge, Boo, Älta and Saltsjöbaden. Van and movers, RUT deduction.",
      h1: "Moving Help Nacka",
    },
    hero: {
      heading: "Moving Help in Nacka",
      intro:
        "From a new build on Kvarnholmen to a waterside plot in Saltsjöbaden — Ungdomskraft moves your home anywhere in Nacka municipality. We carry out in Sickla, Järla, Finntorp, Fisksätra, Orminge, Björknäs or Älta, drive the van to the new address and put everything in place. You see the price when you book and the RUT deduction comes off straight away.",
      heroImageAlt: "Moving help in Nacka — movers loading furniture into a van",
    },
    featureCards: {
      sinceText: "Moving help since 2014",
      storyText:
        "Ungdomskraft has helped people move since 2014. Nacka is a municipality of steep plots, steps down to the water and tower blocks on the hill, and our movers are used to all of it — as well as to the queues on Värmdöleden and when they're worth avoiding.",
      bookYourText: "Book a move in Nacka",
      bookDescText:
        "Tell us where you're moving from and to, what's coming and when. The price shows at once — movers and van included.",
      vehicleTitleText: "We drive, you don't",
      vehicleDescText:
        "The van and the movers come together. We load, drive over Skurubron or in towards the city and unload at the new door, so you don't have to rent or drive anything.",
    },
    itemsSection: {
      heading: "Items we often move in Nacka",
      items: [
        {
          name: "Sofas & armchairs",
          description: "Sofas and armchairs carried up stairs and slopes",
          image: "/sofa.webp",
          alt: "Sofa carried during moving help in Saltsjöbaden, Nacka",
        },
        {
          name: "Beds & mattresses",
          description: "Beds, mattresses and cots for the new bedrooms",
          image: "/bed.webp",
          alt: "Bed loaded during a move from Fisksätra in Nacka",
        },
        {
          name: "Tables & chairs",
          description: "Dining tables, deck furniture and work desks",
          image: "/table.webp",
          alt: "Dining table and chairs during a move in Sickla, Nacka",
        },
        {
          name: "Appliances",
          description: "Washing machine, fridge, freezer and dishwasher",
          image: "/washer.webp",
          alt: "Appliances moved during moving help in Orminge, Nacka",
        },
        {
          name: "Wardrobes & dressers",
          description: "Wardrobes, dressers and shelving systems taken apart",
          image: "/wardrobe.webp",
          alt: "Wardrobe dismantled during a move on Kvarnholmen in Nacka",
        },
        {
          name: "Moving boxes",
          description: "Boxes, boating and summer gear from storage and garage",
          image: "/box.webp",
          alt: "Moving boxes carried during a house move in Boo, Nacka",
        },
      ],
    },
    seoContent: {
      mainHeading: "Moving help in Nacka — new builds on Sicklaön, houses in Boo and Saltsjöbaden",
      mainText:
        "Nacka is one of the municipalities in the county building the most right now, and it shows in the moves we do. On Sicklaön old industrial areas have become homes: Sickla grew up around the former Atlas Copco site, Kvarnholmen has gone from mills and industry to flats by the water, and in Nacka strand office buildings are being converted into housing. At the same time the metro is being extended to Nacka centrum, and new blocks are rising around Järla and Nacka Forum. In the new buildings the lifts are roomy, but loading often goes via a garage or dedicated loading spot, and the moving van needs to be planned in.\n\nFurther out Nacka looks completely different. Fisksätra was built in the 1970s with apartment blocks on the heights above Baggensfjärden, and Orminge in Boo is mainly from the 1960s and 70s. Around them lie house areas in Boo, Björknäs, Älta, Saltsjö-Duvnäs and Saltsjöbaden, where plots often slope and there can be many steps between the street and the entrance. There we carry longer distances, and it's often the basement, the garage and boating gear that make the move big. Typical moves are young people moving into a first flat on Sicklaön, families going from a flat to a house in Boo and older residents swapping a house in Saltsjöbaden for a flat with a lift.\n\nTraffic shapes much of a moving day in Nacka. Värmdöleden runs through the municipality and Skurubron across Skurusundet is the big bottleneck towards Boo, while Södra länken leads into Stockholm from Sickla. Saltsjöbanan runs through the municipality out to Saltsjöbaden and Solsidan, and the cross-city tram reaches Sickla. We route the van so it isn't stuck in rush-hour queues and book the right number of movers for how far we'll carry from the van.",
      preparationHeading: "Prepare your move in Nacka",
      preparationText:
        "Think about the route between van and door. If your house has steps down to it or a long path across the plot, note it in the booking so we send enough movers. Moving into a new build on Sicklaön or Kvarnholmen? Find out how loading to the building works and whether the garage has a height limit.\n\nPack the boxes in advance and label them by room. Gather what's coming from storage, garage and boathouse in one place. Defrost the fridge and freezer the day before and drain the washing machine. Have door codes, keys and garage fobs ready for both addresses.",
      additionalSections: [
        {
          heading: "Districts in Nacka where we move",
          content:
            "We help with moves across the whole of Nacka. On Sicklaön we move in Sickla, Järla, Finntorp, Ektorp, Henriksdal, Jarlaberg, Kvarnholmen, Nacka strand and around Nacka Forum, both in older rental blocks and new builds.\n\nIn Boo we take moves in Orminge, Björknäs, Saltsjö-Boo, Kummelnäs and Tollare. In the south we move in Älta, and in the south-east in Fisksätra, Saltsjöbaden and Solsidan. Driving within 20 km is included, which covers moves within Nacka and to most of Stockholm.",
        },
        {
          heading: "From flat to house — or the other way round",
          content:
            "Many moves in Nacka go between types of home. A family leaves the flat in Sickla for a house in Boo, or a couple sells the house in Saltsjöbaden and moves to a flat near Nacka centrum. There's often more stuff than fits in the new home, and we can take what isn't coming along to recycling or reuse the same day.\n\nMoving help qualifies for the RUT tax deduction. You get 50% off the labour cost, up to SEK 75,000 per person per year, and we deduct it directly on the invoice.",
          cta: { text: "See junk removal in Nacka →", href: "/en/junk-removal-nacka" },
        },
        {
          heading: "Why book moving help in Nacka with Ungdomskraft?",
          listItems: [
            {
              label: "We know Nacka –",
              text: "From the new builds on Kvarnholmen to the sloping plots in Saltsjöbaden. We know where the van can stand and how far it is to carry.",
            },
            {
              label: "Planned driving –",
              text: "We schedule the move around Värmdöleden and Skurubron, so the time goes on carrying, not queuing.",
            },
            {
              label: "Fixed price –",
              text: "You see the price in the booking before confirming. No surcharge for stairs or long carries.",
            },
            {
              label: "Insured –",
              text: "Every move is insured through Trygg Hansa, covering your belongings and the property we carry in.",
            },
            {
              label: "Everything in the van –",
              text: "Van, movers, blankets, straps and floor protection are included.",
            },
          ],
        },
      ],
    },
    faq: [
      {
        question: "How much does moving help in Nacka cost?",
        answer:
          "Single pieces are booked as Small (SEK 995) or Medium (SEK 1,495). A fully furnished studio or one-bed usually costs SEK 2,495–4,995 and a whole house is booked as Mega Large (SEK 8,995). Driving within 20 km is included and you see the price when you book.",
      },
      {
        question: "Can I get the RUT deduction on moving help in Nacka?",
        answer:
          "Yes. You get 50% off the labour cost, up to SEK 75,000 per person per year, and we deduct it directly on the invoice.",
      },
      {
        question: "How quickly can you come to Nacka?",
        answer:
          "We often have slots the same week, sometimes the same day. Pick a date in the booking to see the times.",
      },
      {
        question: "Our house is below a long flight of steps — is that OK?",
        answer:
          "Yes, it's common in Nacka. Note in the booking how far it is from the van to the entrance, and we'll send enough movers.",
      },
      {
        question: "Can you move me into a new build on Kvarnholmen or in Sickla?",
        answer:
          "Yes. Tell us in the booking how loading works, for example via the garage, and whether there's a booked move-in time.",
      },
      {
        question: "Do you move a whole house in Boo or Saltsjöbaden?",
        answer:
          "Yes, with garage, basement and storage. Large houses are booked as Extra Large or Mega Large, and we make several trips if needed.",
      },
      {
        question: "Do you carry in walk-up blocks in Fisksätra and Orminge?",
        answer: "Yes, we carry in stairwells with and without lifts at no extra cost.",
      },
      {
        question: "Do you dismantle and reassemble furniture?",
        answer:
          "Yes, we take apart beds, wardrobes and tables that won't go out whole and put them back together at the new address.",
      },
      {
        question: "Can you move appliances?",
        answer:
          "Yes — washing machines, dryers, fridges, freezers and dishwashers. Defrost the fridge and freezer the day before.",
      },
      {
        question: "Can I move at the weekend?",
        answer: "Yes, we move every day of the week.",
      },
      {
        question: "Can you take away what isn't coming to the new home?",
        answer:
          "Yes, book a junk removal for the same day and we'll take whatever isn't coming to recycling or reuse.",
      },
      {
        question: "Which parts of Nacka do you cover?",
        answer:
          "The whole municipality: Sicklaön (Sickla, Järla, Finntorp, Ektorp, Henriksdal, Jarlaberg, Kvarnholmen, Nacka strand), Boo (Orminge, Björknäs, Saltsjö-Boo, Kummelnäs, Tollare), Älta, Fisksätra and Saltsjöbaden.",
      },
      {
        question: "Which other municipalities do you move in?",
        answer: `We help with moves across the Stockholm region: ${stockholmKommunerText}. Driving within 20 km is always included.`,
      },
    ],
    priceHint: PACKAGE_PRICE_HINT_EN.transport,
    pricingRows: getPackagePricingRows("transport", "en"),
    dynamicHeadings: {
      threeSteps: "Moving help in Nacka in 3 steps",
      step2Text:
        "The movers protect furniture and floors, carry everything out and load the van. We drive to the new address — within Nacka or on into the city.",
      step3Title: "Into the new home",
      step3Desc:
        "We carry in and put everything in the right place. Furniture that was taken apart is reassembled before we leave.",
      ctaHeading: "Book moving help in Nacka",
      ctaText:
        "Fill in addresses, package and time. You see the price instantly and the RUT deduction comes off the invoice.",
    },
  },
};
